import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { CODIGOS_ERRO, codigoDeErro, comCorpoLegivel, detalhesDeErro } from '@core/errors';
import { catchError, switchMap, throwError } from 'rxjs';

/** `detalhes` do 403 `LIBERACAO_INATIVA`; `null` quando a liberação não é do fiscal. */
interface DetalhesLiberacaoInativa {
  status?: string | null;
  acessoInicio?: string | null;
  acessoFim?: string | null;
  encerradaEm?: string | null;
}

/**
 * O acesso do fiscal pode terminar com a tela aberta (o prazo passou, a qualidade encerrou). Qualquer
 * chamada que responda 403 `LIBERACAO_INATIVA` troca a tela pela explicação, em vez de um erro solto.
 * O erro continua para quem chamou.
 *
 * Downloads chegam com o corpo do erro em `Blob`: ele é lido aqui, e o erro segue com o corpo já em
 * JSON, para o `autenticacaoInterceptor` (que roda por fora) enxergar o código e não mandar para
 * /acesso-negado.
 */
export const liberacaoInativaInterceptor: HttpInterceptorFn = (requisicao, next) => {
  const router = inject(Router);
  return next(requisicao).pipe(
    catchError((erro: unknown) => {
      if (!(erro instanceof HttpErrorResponse) || erro.status !== 403) return throwError(() => erro);
      return comCorpoLegivel(erro).pipe(
        switchMap((legivel) => {
          if (codigoDeErro(legivel) === CODIGOS_ERRO.LIBERACAO_INATIVA) {
            void router.navigate(['/fiscalizacao/acesso-encerrado'], {
              queryParams: parametrosDaTela(detalhesDeErro<DetalhesLiberacaoInativa>(legivel)),
            });
          }
          return throwError(() => legivel);
        }),
      );
    }),
  );
};

/** Só as chaves com valor: a tela escolhe a frase pelo `status` e mostra a data que explica o motivo. */
function parametrosDaTela(detalhes: DetalhesLiberacaoInativa | null): Record<string, string> {
  const parametros: Record<string, string> = {};
  const incluir = (chave: string, valor: string | null | undefined) => {
    if (valor) parametros[chave] = valor;
  };
  incluir('status', detalhes?.status);
  incluir('inicio', detalhes?.acessoInicio);
  incluir('ate', detalhes?.acessoFim);
  incluir('encerradaEm', detalhes?.encerradaEm);
  return parametros;
}
